import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react'
import PropTypes from 'prop-types'
import AggregationBar from 'actionHub/views/AggregationBar/AggregationBar'
import useAction from 'actionHub/hooks/useAction'
import { hot } from 'react-hot-loader/root'
import TaskList from 'actionHub/views/TaskList'
import { jobPermissions } from 'utils/permissions'
import { useJobPropTypes } from 'hooks/useJob'
import BulkUpdateModal from 'actionHub/views/BulkUpdateModal/BulkUpdateModal'
import { useDispatch, useSelector } from 'react-redux'
import { actionHubUpdateSelectedTasks, actionHubResetBulkUpdateTaskToggle } from 'actionHub/redux/actions'
import Loading from 'components/Loading'
import DialogSimple from 'components/DialogSimple'
import Spinner from 'components/Spinner'
import TabFilter from 'actionHub/views/TabFilter'
import classNames from 'classnames'
import TickmarkHolder from 'actionHub/views/TickmarkHolder'
import { Divider } from '@material-ui/core'
import ExpandContainer from 'components/ExpandContainer'
import { Heading } from '@deloitte/gel-library'
import constants from 'constants'
import { modalCreate, modalShow } from 'actions/modals'
import useApp from 'hooks/useApp'
import EditDetailsForm from 'actionHub/views/EditDetailsForm'

import ListAltIcon from '@material-ui/icons/ListAlt'
import CancelIcon from '@material-ui/icons/Cancel'
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled'
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd'
import LabelOffIcon from '@material-ui/icons/LabelOff'
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle'

import styles from './ActionTasks.scss'

function ActionTasks({ useJob, match: { params: { actionSetId } } }) {
  const {
    actionStates,
    addNewTask,
    allowTags,
    columns,
    detailColumns,
    fetchNextPage,
    filterStatus,
    globalFilters,
    goToTag,
    goToTask,
    isCurrent,
    isFetching,
    isSubmittingTaskLabels,
    mainAggregation,
    onTabChange,
    priorities,
    selectedTab,
    setTasksActionState,
    setTasksAssigned,
    setTasksPriority,
    statuses,
    tags,
    tasks,
  } = useAction(actionSetId)

  const {
    clientName,
    deloitteTeam,
    engagement,
    externalTeam,
    isAssignedActionsEditor,
    jobName,
    userHasJobPermission,
  } = useJob

  const { user } = useApp()
  const dispatch = useDispatch()
  const { packageName } = useSelector(state => state.job)
  const modals = useSelector(state => state.modals)
  const {
    _isSelectedTasksUpdating: isBulkUpdateLoading,
    selectedTasks,
    taskListSearchQuery,
  } = useSelector(state => state.actionHub)
  const [expanded, setExpanded] = useState(true)
  const [localSelectedTasks, setLocalSelectedTasks] = useState(Object.values(selectedTasks).map(task => task._id))

  const BULK_UPDATE_MODAL_ID = constants.MODAL_BULK_ACTION_UPDATES
  const ADD_TASK_MODAL_ID = constants.MODAL_ADD_TASK

  useEffect(() => {
    dispatch(actionHubUpdateSelectedTasks(actionSetId, localSelectedTasks))
  }, [actionSetId, dispatch, localSelectedTasks])

  const canEditActions = userHasJobPermission(jobPermissions.actionHubEdit)
  const isControlsAdvantage = packageName.toLowerCase() === 'ControlsAdvantage'.toLowerCase()

  const createModal = useCallback(() => {
    if (modals && !modals.modalNames.includes(BULK_UPDATE_MODAL_ID)) {
      dispatch(modalCreate(BULK_UPDATE_MODAL_ID))
    }
  }, [BULK_UPDATE_MODAL_ID, dispatch, modals])

  const onHandleOpenModal = useCallback((isBulkUpdate, taskId) => {
    if (isBulkUpdate) {
      dispatch(actionHubUpdateSelectedTasks(actionSetId, localSelectedTasks))
      dispatch(actionHubResetBulkUpdateTaskToggle(actionSetId, localSelectedTasks))
      dispatch(modalShow(BULK_UPDATE_MODAL_ID))
    } else {
      goToTask(taskId)
    }
  }, [BULK_UPDATE_MODAL_ID, actionSetId, dispatch, goToTask, localSelectedTasks])

  const handleAddTaskClick = useCallback(() => {
    if (modals && !modals.modalNames.includes(ADD_TASK_MODAL_ID)) {
      dispatch(modalCreate(ADD_TASK_MODAL_ID))
    }
    dispatch(modalShow(ADD_TASK_MODAL_ID))
  }, [ADD_TASK_MODAL_ID, dispatch, modals])

  const filteredData = useMemo(() => {
    let filteredTasks = tasks
    switch (selectedTab) {
      case 'assignedToMe':
        filteredTasks = tasks.filter(x => x.assignedTo === user.id)
        break
      case 'incomplete':
        filteredTasks = tasks.filter(x => x.statusId !== 3)
        break
      case 'toBeReviewed':
        filteredTasks = tasks.filter(x => x.taskActionId === 1)
        break
      case 'excluded':
        filteredTasks = tasks.filter(x => x.taskActionId === 3)
        break
      case 'noTag':
        filteredTasks = tasks.filter(x => x.tagId === null)
        break
      case 'withClient':
        filteredTasks = tasks.filter(x => x.commentStatusId === 2)
        break
      default:
        break
    }

    if (!taskListSearchQuery || taskListSearchQuery.length < 3) {
      return filteredTasks
    }

    filteredTasks = filteredTasks.filter(item => Object.values(item).some(value => (typeof value === 'string' && value.toLowerCase().includes(taskListSearchQuery.toLowerCase()))
      || (typeof value === 'object' && value !== null && Object.values(value).some(
        nestedValue => typeof nestedValue === 'string' && nestedValue.toLowerCase().includes(taskListSearchQuery.toLowerCase()),
      ))))

    return filteredTasks
  }, [taskListSearchQuery, selectedTab, tasks, user.id])

  const aggregation = {
    ...mainAggregation,
    denominator: mainAggregation.totalActions,
    filteredTasksCount: filteredData.length || 0,
    value: statuses.find(x => x.id === 3).count,
    valueDescription: '$VALUE$ reviewed',
    secondaryValue: statuses.find(x => x.id === 0).count,
    secondaryValueDescription: '$VALUE$ excluded from review',
  }

  const tabProperties = {
    all: { name: 'All', displayOrder: 1, icon: <ListAltIcon /> },
    incomplete: { name: 'Incomplete', displayOrder: 2, icon: <PlayCircleFilledIcon /> },
    assignedToMe: { name: 'Assigned to me', displayOrder: 3, icon: <AssignmentIndIcon /> },
    toBeReviewed: { name: 'To be reviewed', displayOrder: 4, icon: <ListAltIcon /> },
    ignored: { name: 'Ignored', displayOrder: 5, icon: <CancelIcon /> },
    noTag: { name: 'No tickmark', displayOrder: 5, icon: <LabelOffIcon /> },
    withClient: { name: 'With client', displayOrder: 6, icon: <SupervisedUserCircleIcon /> },
  }

  const tabs = [
    ...Object.keys(globalFilters).map(k => ({
      id: k,
      count: globalFilters[k],
      ...tabProperties[k],
    })),
  ]

  const tagClicked = (tag) => {
    onTabChange(null)
    goToTag(tag.id)
  }

  return (
    <div className={styles.flexContainer}>
      {!isAssignedActionsEditor && (
        <div className={classNames(styles.sideNav, styles.flex)}>
          <div className={styles.heading}>
            CASES
          </div>
          <div>
            <TabFilter
              className={styles.tabs}
              data={tasks}
              onTabChange={onTabChange}
              startingTabId={selectedTab}
              tabs={tabs}
            />
          </div>
          <Divider />
          <div className={styles.tagContainer}>
            {allowTags
            && (
              <>
                <div className={styles.heading}>
                  TICKMARKS
                </div>
                <TickmarkHolder clicked={tag => tagClicked(tag)} tags={tags} />
              </>
            )}
          </div>
          <ExpandContainer
            className={styles.expandContainer}
            expanded={!expanded}
            heading={<div>Job details</div>}
            setExpanded={() => setExpanded(!expanded)}
          >
            <div className={styles.jobDetailHeadings}>Job name</div>
            <div className={styles.jobDetailsValues}>{jobName}</div>
            <div className={styles.jobDetailHeadings}>Client</div>
            <div className={styles.jobDetailsValues}>{clientName}</div>
            {!user.isExternal && (
            <div>
              <div className={styles.jobDetailHeadings}>Engagement</div>
              <div className={styles.jobDetailsValues}>
                {engagement.code}
                {' '}
                {engagement.name}
              </div>
            </div>
            )}
            <div className={styles.jobDetailHeadings}>Solution name</div>
            <div>{packageName}</div>
          </ExpandContainer>
        </div>
      )}

      <div className={classNames(styles.mainScreen, styles.flex)}>
        {isFetching ? (
          <Loading pageLoading />
        ) : (
          <>
            <AggregationBar
              aggregations={[aggregation]}
              handleAddTaskClick={handleAddTaskClick}
              showAddTaskButton={isControlsAdvantage && !isAssignedActionsEditor}
            />
            <TaskList
              actionSetId={actionSetId}
              actionStates={actionStates}
              canLoadMore={filterStatus.hasNext && !isFetching}
              clients={externalTeam}
              columns={columns}
              disabled={!isCurrent || !canEditActions}
              goToTask={goToTask}
              hideTaskMeta={isAssignedActionsEditor}
              members={deloitteTeam}
              onHandleOpenModal={onHandleOpenModal}
              onLoadMore={fetchNextPage}
              onTabChange={onTabChange}
              priorities={priorities}
              selectedFilter={selectedTab}
              selectedTasks={localSelectedTasks}
              setSelectedTasks={(taskIds) => {
                createModal()
                setLocalSelectedTasks(taskIds)
              }}
              setTasksActionState={setTasksActionState}
              setTasksAssigned={setTasksAssigned}
              setTasksPriority={setTasksPriority}
              showTags={allowTags}
              statuses={statuses}
              tasks={filteredData}
            />
          </>
        )}
      </div>

      {localSelectedTasks.length > 0 && modals.modalNames.includes(BULK_UPDATE_MODAL_ID)
        && (
        <BulkUpdateModal
          actionSetId={actionSetId}
          modalId={BULK_UPDATE_MODAL_ID}
          selectedTasks={localSelectedTasks}
          setSelectedTasks={setLocalSelectedTasks}
          useJob={useJob}
        />
        )}
      {detailColumns && modals.modalNames.includes(ADD_TASK_MODAL_ID)
        && (
        <EditDetailsForm
          isNewForm
          columns={detailColumns}
          modalId={ADD_TASK_MODAL_ID}
          onSaveClick={addNewTask}
        />
        )}

      <DialogSimple
        hideFooter
        className={styles.updatingDialog}
        isOpen={isSubmittingTaskLabels}
      >
        <Heading className={styles.updatingText} level={8}>Updating task labels..</Heading>
        <div className={styles.spinner}><Spinner /></div>
      </DialogSimple>
    </div>
  )
}

ActionTasks.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      actionSetId: PropTypes.string,
    }),
  }).isRequired,
  useJob: useJobPropTypes.isRequired,
}

export default hot(ActionTasks)

